import React, { memo, useMemo, useState, useRef, useEffect } from "react"; // useRef kept for drag-and-drop
import { useLocation } from "react-router-dom";
import { Icon, IconButton, Chip } from "../UIComponents";

const exportToExcel = async (rows, exportColumns, filename = "export") => {
  try {
    if (!window.ExcelJS) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js";
        script.onload  = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    const workbook  = new window.ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Data");

    // Column widths + header text
    worksheet.columns = exportColumns.map((col) => ({
      header: col.title,
      key:    col.key,
      width:  Math.min(Math.max(col.title.length + 4, 14), 60),
    }));

    const thin   = { style: "thin", color: { argb: "FF000000" } };
    const border = { top: thin, left: thin, bottom: thin, right: thin };

    // Style header row — yellow background, bold, bordered
    for (let c = 1; c <= exportColumns.length; c++) {
      const cell    = worksheet.getRow(1).getCell(c);
      cell.font     = { bold: true, color: { argb: "FF000000" } };
      cell.fill     = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF00" } };
      cell.border   = border;
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    }

    // Add data rows with borders
    rows.forEach((row) => {
      const values = exportColumns.reduce((obj, col) => {
        let val = row[col.key];
        if (col.format) val = col.format(val, row);
        else if (Array.isArray(val)) val = val.join(", ");
        obj[col.key] = val == null ? "" : String(val);
        return obj;
      }, {});
      const dataRow = worksheet.addRow(values);
      for (let c = 1; c <= exportColumns.length; c++) {
        const cell     = dataRow.getCell(c);
        cell.border    = border;
        cell.alignment = { vertical: "middle", wrapText: true };
      }
    });

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob   = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement("a");
    a.href       = url;
    a.download   = `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // CSV fallback
    const headers  = exportColumns.map((c) => c.title).join(",");
    const csvRows  = rows.map((row) =>
      exportColumns.map((col) => {
        let v = row[col.key];
        if (col.format) v = col.format(v, row);
        else if (Array.isArray(v)) v = v.join("; ");
        return `"${String(v ?? "").replace(/"/g, '""')}"`;
      }).join(",")
    );
    const blob = new Blob([[headers, ...csvRows].join("\n")], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement("a"), { href: url, download: `${filename}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }
};

// TableToolbar — Search always visible + Export
const TableToolbar = memo(({
  searchQuery = "",
  onSearchChange,
  exportColumns = [],
  allFilteredRows = [],
  exportFilename = "export",
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!exportColumns.length || !allFilteredRows.length) return;
    setExporting(true);
    await exportToExcel(allFilteredRows, exportColumns, exportFilename);
    setExporting(false);
  };

  return (
    <div className="px-5 py-3 border-b border-[#ebeeee] bg-white">
      <div className="flex justify-end items-center gap-3">
        {/* Search — always visible */}
        <div className="relative w-72">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-[16px]">
            search
          </span>
          <input
            type="text"
            placeholder="Search…"
            value={searchQuery}
            onChange={onSearchChange}
            style={{ cursor: "pointer" }}
            className="w-full pl-9 pr-9 py-1.5 text-sm border border-[#bdc9c9] rounded-lg
                       bg-white text-[#181c1d]
                       focus:outline-none focus:ring-2 focus:ring-[#005b60] focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange?.({ target: { value: "" } })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#181c1d]"
              aria-label="Clear search"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          )}
        </div>

        {searchQuery && (
          <span className="text-xs text-[#3e494a]">
            {allFilteredRows.length} result{allFilteredRows.length !== 1 ? "s" : ""} found
          </span>
        )}

        <button
          onClick={handleExport}
          disabled={exporting || allFilteredRows.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-[#e5e9e9] rounded-lg text-sm font-semibold
                     text-[#3e494a] hover:bg-[#d8dcdc] transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Exporting…</>
          ) : (
            <><span className="material-symbols-outlined text-[18px]">file_download</span>Export</>
          )}
        </button>
      </div>
    </div>
  );
});
TableToolbar.displayName = "TableToolbar";

// RecordCount — sits between table and pagination
const RecordCount = memo(({ currentPage, pageSize, totalCount }) => {
  const rangeStart = totalCount === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, totalCount);
  const rangeEnd   = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="px-5 py-1.5 border-t border-[#ebeeee] bg-white flex justify-end">
      <span className="text-xs text-[#3e494a]">
        {totalCount === 0
          ? "No records"
          : `Showing ${rangeStart}–${rangeEnd} of ${totalCount} records`}
      </span>
    </div>
  );
});
RecordCount.displayName = "RecordCount";

const EmptyRow = memo(({ colSpan, message }) => (
  <tr>
    <td colSpan={colSpan} className="px-6 py-8 text-center text-sm text-[#3e494a]">
      {message}
    </td>
  </tr>
));
EmptyRow.displayName = "EmptyRow";

const TableHeader = memo(({ columns, hasActions, sortable }) => (
  <thead className="bg-[#f1f4f4]">
    <tr>
      {sortable && <th className="w-10" />}
      {columns.map((col) => (
        <th
          key={col.key}
          scope="col"
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#3e494a]
            ${col.align === "right" ? "text-right" : ""}`}
        >
          {col.title}
        </th>
      ))}
      {hasActions && (
        <th scope="col" className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#3e494a] text-right">
          Actions
        </th>
      )}
    </tr>
  </thead>
));
TableHeader.displayName = "TableHeader";

const TableRow = memo(({ row, columns, onEdit, onDelete, rowActions }) => {
  const hasActions = Boolean(onEdit || onDelete || rowActions);
  return (
    <tr className="hover:bg-[#f1f4f4] transition-colors group">
      {columns.map((col) => (
        <td
          key={col.key}
          className={`px-5 py-1.5 text-sm text-[#181c1d] whitespace-pre-wrap ${col.align === "right" ? "text-right" : ""}`}
        >
          {col.render ? col.render(row) : row[col.key]}
        </td>
      ))}
      {hasActions && (
        <td className="px-5 py-1.5 text-right">
          <div className="flex justify-end gap-1">
            {rowActions ? rowActions(row) : (
              <>
                {onEdit   && <IconButton icon="edit"   label="Edit"   onClick={() => onEdit(row)} />}
                {onDelete && <IconButton icon="delete" label="Delete" onClick={() => onDelete(row)} colorClass="text-[#3e494a] hover:text-red-600" />}
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
});
TableRow.displayName = "TableRow";

const DraggableRow = memo(({ row, columns, onEdit, onDelete, rowActions, isOver, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const hasActions = Boolean(onEdit || onDelete || rowActions);
  return (
    <tr
      onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`transition-colors group ${isOver ? "bg-[#08757C]/5 border-l-4 border-[#08757C]" : "hover:bg-[#f1f4f4]"}`}
    >
      <td
        draggable
        onDragStart={onDragStart}
        className="pl-4 py-2.5 w-8"
        style={{ cursor: "grab" }}
      >
        <span className="material-symbols-outlined text-[19px] text-[#bdc9c9] select-none">
          drag_indicator
        </span>
      </td>
      {columns.map((col) => (
        <td
          key={col.key}
          className={`px-5 py-1.5 text-sm text-[#181c1d] whitespace-pre-wrap ${col.align === "right" ? "text-right" : ""}`}
        >
          {col.render ? col.render(row) : row[col.key]}
        </td>
      ))}
      {hasActions && (
        <td className="px-5 py-1.5 text-right">
          <div className="flex justify-end gap-1">
            {rowActions ? rowActions(row) : (
              <>
                {onEdit   && <IconButton icon="edit"   label="Edit"   onClick={() => onEdit(row)} />}
                {onDelete && <IconButton icon="delete" label="Delete" onClick={() => onDelete(row)} colorClass="text-[#3e494a] hover:text-red-600" />}
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
});
DraggableRow.displayName = "DraggableRow";

export const ChipFilterBar = memo(({ chips = [], activeChip, onChipSelect, searchQuery = "", onSearchChange }) => {
  if (!chips.length) return null;
  return (
    <div className="px-5 py-3 border-b border-[#ebeeee] bg-[#f7fafa] rounded-t-2xl">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Chip key={chip.id} active={activeChip?.id === chip.id} onClick={() => onChipSelect?.(chip)}>
              {chip.label}
            </Chip>
          ))}
        </div>
        {typeof onSearchChange === "function" && (
          <div className="relative w-64 flex-shrink-0">
            <input
              type="text" placeholder="Search…" value={searchQuery} onChange={onSearchChange}
              className="w-full px-4 py-2 pl-10 pr-10 border border-[#bdc9c9] rounded-lg bg-white text-[#181c1d] text-sm focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:border-transparent"
            />
            <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888780] text-[16px]" />
            {searchQuery && (
              <button onClick={() => onSearchChange({ target: { value: "" } })} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#181c1d] transition-colors" aria-label="Clear search">
                <Icon name="close" className="text-[16px]" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
ChipFilterBar.displayName = "ChipFilterBar";

export const Pagination = memo(({ currentPage, totalPages, pageSize, pageSizes = [10, 25, 50], onPageChange, onPageSizeChange }) => {
  const pages = useMemo(() => Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1), [totalPages]);
  return (
    <div className="px-5 py-3 bg-[#f1f4f4]/50 flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="flex items-center gap-2 text-[#3e494a] text-sm">
        <span>Rows per page:</span>
        <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="bg-white border-none rounded-lg text-xs font-bold py-1 px-3 focus:ring-1 focus:ring-[#005b60] shadow-sm cursor-pointer outline-none">
          {pageSizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <nav className="flex items-center gap-1">
        <PaginationButton icon="first_page"    label="First page"    onClick={() => onPageChange(1)}                               disabled={currentPage <= 1} />
        <PaginationButton icon="chevron_left"  label="Previous page" onClick={() => onPageChange(Math.max(1, currentPage - 1))}   disabled={currentPage <= 1} />
        <div className="flex gap-1 px-1">
          {pages.map((page) => (
            <button key={page} type="button" onClick={() => onPageChange(page)}
              className={`w-8 h-8 rounded-lg font-bold text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005b60]
                ${page === currentPage ? "bg-[#005b60] text-white" : "hover:bg-[#e5e9e9] text-[#3e494a]"}`}>
              {page}
            </button>
          ))}
          {totalPages > pages.length && <span className="px-1 self-end text-[#3e494a] text-xs">…</span>}
        </div>
        <PaginationButton icon="chevron_right" label="Next page"  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages} />
        <PaginationButton icon="last_page"     label="Last page"  onClick={() => onPageChange(totalPages)}                             disabled={currentPage >= totalPages} />
      </nav>
    </div>
  );
});
Pagination.displayName = "Pagination";

const PaginationButton = memo(({ icon, label, onClick, disabled }) => (
  <button type="button" aria-label={label} onClick={onClick} disabled={disabled}
    className="w-8 h-8 flex items-center justify-center rounded text-[#3e494a] hover:text-[#005b60] transition-colors disabled:opacity-30 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005b60]">
    <Icon name={icon} className="text-[18px]" />
  </button>
));
PaginationButton.displayName = "PaginationButton";

// ConfigTable — main export
const ConfigTable = memo(({
  columns = [],
  rows = [],
  onEdit,
  onDelete,
  rowActions,
  emptyText = "No records found.",
  className = "",
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  pageSizes = [10, 25, 50],
  onPageChange,
  onPageSizeChange,
  searchQuery = "",
  onSearchChange,
  allFilteredRows = [],
  exportColumns = [],
  exportFilename = "export",
  // ── Drag-and-drop sort ──
  sortable = false,
  onSortEnd,
  isSavingOrder = false,
  showPagination,        // override: true = always show, false = always hide, undefined = !sortable
  // Key used to remember the current page across unmount/remount (e.g. when
  // navigating away to an edit screen and back). Defaults to the route path
  // so each list page remembers its own page independently.
  persistKey,
}) => {
  const hasActions = Boolean(onEdit || onDelete || rowActions);
  const totalCount = allFilteredRows.length;

  // ── Remember page across navigation (sessionStorage) ──
  const storageKey = `configtable_page_${persistKey ?? (typeof window !== "undefined" ? window.location.pathname : "default")}`;
  const routerLocation = useLocation();
  const cameFromEditRefresh = Boolean(routerLocation.state?.refresh);

  // Restore only when arriving back from a save/edit (the `refresh` state
  // flag set by the edit pages) — NOT when the page is freshly visited via
  // normal navigation (switching tabs, coming from another page, etc.),
  // which should always start at page 1.
  useEffect(() => {
    if (!cameFromEditRefresh) return;
    if (currentPage !== 1) return; // parent already has a specific page — don't override
    const saved = Number(sessionStorage.getItem(storageKey));
    if (saved && saved > 1 && onPageChange) {
      onPageChange(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep sessionStorage in sync with whatever page the parent is on,
  // whether that came from clicking pagination or from a filter/search reset.
  useEffect(() => {
    sessionStorage.setItem(storageKey, String(currentPage));
  }, [currentPage, storageKey]);

  // Drag state — only used when sortable=true
  const dragIdx     = useRef(null);
  const dragOverIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);
  const rafRef      = useRef(null);

  // Auto-scroll while dragging near top / bottom edge of the viewport
  useEffect(() => {
    if (!sortable) return;
    const ZONE = 80; // px from edge that triggers scroll
    const SPEED = 10; // px per frame

    const onDragOver = (e) => {
      cancelAnimationFrame(rafRef.current);
      const { clientY } = e;
      const { innerHeight } = window;
      if (clientY < ZONE) {
        const step = () => { window.scrollBy(0, -SPEED); rafRef.current = requestAnimationFrame(step); };
        rafRef.current = requestAnimationFrame(step);
      } else if (clientY > innerHeight - ZONE) {
        const step = () => { window.scrollBy(0, SPEED); rafRef.current = requestAnimationFrame(step); };
        rafRef.current = requestAnimationFrame(step);
      }
    };

    const onDragEnd = () => cancelAnimationFrame(rafRef.current);

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend",  onDragEnd);
    document.addEventListener("drop",     onDragEnd);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend",  onDragEnd);
      document.removeEventListener("drop",     onDragEnd);
      cancelAnimationFrame(rafRef.current);
    };
  }, [sortable]);

  const handleDragStart = (i)  => { dragIdx.current = i; };
  const handleDragOver  = (i)  => { dragOverIdx.current = i; setOverIdx(i); };
  const handleDragEnd   = ()   => { setOverIdx(null); dragIdx.current = null; dragOverIdx.current = null; };
  const handleDrop      = ()   => {
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    setOverIdx(null);
    dragIdx.current = null;
    dragOverIdx.current = null;
    if (from === null || to === null || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onSortEnd?.(next);
  };

  return (
    <section
      className={`bg-white rounded-2xl overflow-hidden border border-[#ebeeee] ${className}`}
      aria-label="Data table"
    >
      {/* ── Toolbar ── */}
      <TableToolbar
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          exportColumns={exportColumns}
          allFilteredRows={allFilteredRows}
          exportFilename={exportFilename}
        />

      {/* ── Drag hint (sortable mode only) ── */}
      {sortable && (
        <div className="px-5 py-1.5 border-b border-[#ebeeee] bg-[#f7fafa] flex items-center gap-2">
          {isSavingOrder ? (
            <span className="flex items-center gap-1.5 text-xs text-[#08757C] font-semibold">
              <span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>
              Saving order…
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-[#6e797a]">
              <span className="material-symbols-outlined text-[13px]">drag_indicator</span>
              Drag rows to reorder priority
            </span>
          )}
        </div>
      )}

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <TableHeader columns={columns} hasActions={hasActions} sortable={sortable} />
          <tbody className="divide-y divide-[#ebeeee]">
            {rows.length === 0
              ? <EmptyRow colSpan={columns.length + (hasActions ? 1 : 0) + (sortable ? 1 : 0)} message={emptyText} />
              : rows.map((row, i) => sortable ? (
                  <DraggableRow
                    key={row.id ?? i}
                    row={row}
                    columns={columns}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    rowActions={rowActions}
                    isOver={overIdx === i}
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={() => handleDragOver(i)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  />
                ) : (
                  <TableRow
                    key={row.id ?? JSON.stringify(row)}
                    row={row}
                    columns={columns}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    rowActions={rowActions}
                  />
                ))
            }
          </tbody>
        </table>
      </div>

      {/* ── Record count + Pagination ── */}
      {(showPagination ?? !sortable) && (
        <>
          <RecordCount currentPage={currentPage} pageSize={pageSize} totalCount={totalCount} />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizes={pageSizes}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      )}
    </section>
  );
});
ConfigTable.displayName = "ConfigTable";

export default ConfigTable;
